import { Flex, SimpleGrid, Text, VStack, useDisclosure } from '@chakra-ui/react';
import { Form, Formik, FormikHelpers } from 'formik';
// eslint-disable-next-line no-restricted-imports
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';

import { useLEAccordionItemContext } from '../../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import Loader from '../../../../components/Loader';
import Button from '../../../../components/ui/buttons/Button';
import PrimaryButton from '../../../../components/ui/buttons/PrimaryButton';
import {
  OtherLienholder,
  OtherLienholderSlug,
  isTreatedAsOther,
} from '../../../../constants/lienholders';
import {
  UNABLE_TO_ASSIST_ERROR_MSG,
  UNSUPPORTED_LIENHOLDER_SELL_MSG,
} from '../../../../constants/tempInfos';
import { useTemporaryInfoUpdateMutation } from '../../../../gql/generated/graphql';
import { Lienholder } from '../../../../gql/prs/types';
import { useStep } from '../../../../hooks';
import { CookieKeys, useCookie } from '../../../../hooks/useCookie';
import useImageLoader from '../../../../hooks/useImageLoader';
import useIsSellBlockedLienholder from '../../../../hooks/useIsSellBlockedLienholder';
import useIsSellingAvailable from '../../../../hooks/useIsSellingAvailable';
import useLienholders from '../../../../hooks/useLienholders';
import useOptOutLookup from '../../../../hooks/useOptOutLookup';
import { usePostLienholderRouting } from '../../../../hooks/usePostLienholderRouting';
import { useTempInfo } from '../../../../hooks/useTempInfo';
import { trackLead } from '../../../../services/analytics/conversionEvents';
import { RudderEvent, rudderanalytics } from '../../../../services/analytics/rudderstack';
import ChangedLienholderModal, { checkLienholderDifference } from './ChangedLienholderModal';
import LienholderModal from './LienholderModal';
import LienholderSelector from './LienholderSelector';
import { lienholderNotAvailableInState } from './utils';

interface FormValues {
  lienholder: string;
  other_lienholder: string;
}

interface WhoIsYourLeaseProps {
  clearUnsupportedSellError?: () => void;
  onBuyout: () => Promise<void>;
}

const WhoIsYourLease = ({ clearUnsupportedSellError, onBuyout }: WhoIsYourLeaseProps) => {
  const { state, updateState, index, setOpenAccordionIndex, isExpanded } =
    useLEAccordionItemContext();
  const [refresh, setRefresh] = useState<boolean>(false);
  const { info, infoLoading, refetch } = useTempInfo({
    fetchPolicy: 'network-only',
    refresh: refresh && isExpanded,
  });
  const tempInfoData = info?.data;
  const ldClient = useLDClient();
  const [accessToken] = useCookie<string>(CookieKeys.ACCESS_TOKEN);
  const isAuthenticated = !!accessToken;
  const step = useStep();
  const resolveRouting = usePostLienholderRouting();
  const keepMyCarSelectorEnabled = useIsSellingAvailable(tempInfoData);
  const isSellBlockedLienholder = useIsSellBlockedLienholder();

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const checkOptOut = useOptOutLookup();

  useEffect(() => {
    if (state.index === index) {
      ldClient?.track('Lienholder-Panel-Viewed', {});
    }
  }, [ldClient, state]);

  const {
    lienholders,
    probableLienholders,
    loading: lienholdersLoading,
  } = useLienholders({
    make: tempInfoData?.make,
  });

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [blockedLienholder, setBlockedLienholder] = useState<string | null>(null);

  const probableLienholdersDisplayNames = probableLienholders
    .map((pLh: Lienholder) => pLh.display_name)
    .sort();

  const resolveNextStep = async ({
    skipPayoffAccordion,
    wantsToSell,
  }: {
    skipPayoffAccordion: boolean;
    wantsToSell: boolean;
  }) => {
    updateState({ status: 'complete' });
    // Authenticated sellers still need to answer the keep/sell question, which
    // now lives in a dedicated final panel. Open it instead of finishing here.
    if (isAuthenticated && keepMyCarSelectorEnabled) {
      setOpenAccordionIndex(index + 1);
      return;
    }
    if (isAuthenticated) {
      await checkOptOut({ email: tempInfoData?.email });
      trackLead({ tempInfoId: info?.id, email: tempInfoData?.email ?? undefined });
      step.moveNext({ skipPayoffAccordion, wantsToSell });
    } else {
      setOpenAccordionIndex(index + 1);
    }
  };

  const {
    isOpen: isDifferentLienholderModalOpen,
    onClose: onDifferentLienholderModalClose,
    onOpen: onDifferentLienholderModalOpen,
  } = useDisclosure();

  const onSubmit = async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    // Always clear the downstream sell error: picking a supported lienholder via
    // the normal selector skips the checkLienholderDifference path below, so
    // clearing only there would leave a stale error in PersonalInfo.
    clearUnsupportedSellError?.();
    setBlockedLienholder(null);
    const selectedLh = lienholders.find((lh) => lh.display_name === values.lienholder);
    const isVin = !tempInfoData?.license_plate_number;

    if (
      lienholderNotAvailableInState(
        isVin ? null : tempInfoData?.license_plate_state,
        selectedLh?.slug,
      )
    ) {
      formikHelpers.setFieldError('lienholder', UNABLE_TO_ASSIST_ERROR_MSG);
      setIsModalOpen(false);
      return;
    }

    if (isDifferentLienholderModalOpen) {
      onDifferentLienholderModalClose();
    } else if (
      info &&
      checkLienholderDifference(info, values.lienholder, values.other_lienholder)
    ) {
      onDifferentLienholderModalOpen();
      return;
    }

    const selectedLienholder = lienholders.find(
      (lh: Lienholder) => lh.display_name === values.lienholder,
    );
    const lienholderLogo = selectedLienholder?.logo_url ?? '';
    const selectedLienholderSource = {
      lienholder: values.lienholder,
      lienholder_slug: selectedLienholder?.slug,
      is_core: selectedLienholder?.is_core,
    };
    const treatedAsOther = isTreatedAsOther(selectedLienholderSource);
    // Non-core selections are stored as Other (slug "other" + the name in
    // other_lienholder), so downstream sees exactly what a manual entry produces.
    const lienholderSlug = treatedAsOther ? OtherLienholderSlug : selectedLienholder?.slug ?? '';

    const { data: updatedInfo } = await updateTemporaryInfo({
      variables: {
        info: {
          id: info?.id,
          data: {
            lienholder: values.lienholder,
            lienholder_slug: lienholderSlug,
            // A non-core selection mirrors a manual "Other" entry: its name is
            // persisted as other_lienholder for downstream consumers (deal notes).
            other_lienholder:
              values.lienholder === OtherLienholder
                ? values.other_lienholder
                : treatedAsOther
                ? values.lienholder
                : '',
            lienholderLogo,
          },
        },
      },
    });
    const updatedTempInfoData = updatedInfo?.temporaryInfoUpdate?.data ?? tempInfoData ?? null;

    setIsModalOpen(false);

    const wantsToSell = keepMyCarSelectorEnabled && updatedTempInfoData?.wants_to_sell === true;

    if (wantsToSell && isSellBlockedLienholder(selectedLienholderSource)) {
      await onBuyout();
      setBlockedLienholder(values.lienholder);
      await refetch();
      return;
    }

    const { skipPayoffAccordion } = await resolveRouting({
      wantsToSell,
      tempInfoData: updatedTempInfoData,
    });

    await resolveNextStep({ skipPayoffAccordion, wantsToSell });
  };

  const validationSchema = Yup.object({
    lienholder: Yup.string().required('Please select a lienholder'),
    other_lienholder: Yup.string().when('lienholder', {
      is: OtherLienholder,
      then: (schema) => schema.required('Please enter lienholder name'),
    }),
  });

  useEffect(() => {
    if (isExpanded && (!tempInfoData?.state || !tempInfoData?.lienholder)) {
      setRefresh(!refresh);
    }
  }, [isExpanded, info]);

  const loadingImages = useImageLoader(probableLienholders.map((lh) => lh.logo_url));

  if (lienholdersLoading || infoLoading || loadingImages) {
    return <Loader />;
  }

  return (
    <VStack fontSize={16} color="leaseEndBlue" mt={2} textAlign="center">
      <Formik
        initialValues={{
          lienholder: tempInfoData?.lienholder ?? '',
          other_lienholder: tempInfoData?.other_lienholder ?? '',
        }}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize
        onSubmit={onSubmit}
      >
        {({ isSubmitting, handleSubmit, submitForm, errors, values }) => {
          const showUnsupportedSellError = blockedLienholder === values.lienholder;
          return (
            <Form>
              <Flex direction="column" alignItems="center">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="22px">
                  {probableLienholders.map((probableLh) => (
                    <LienholderSelector
                      key={probableLh?.display_name}
                      lienholder={probableLh?.display_name}
                      lienholderLogo={probableLh?.logo_url}
                    />
                  ))}
                  {tempInfoData?.lienholder &&
                    !probableLienholdersDisplayNames?.includes(tempInfoData.lienholder) && (
                      <LienholderSelector
                        key={tempInfoData.lienholder}
                        lienholder={tempInfoData.lienholder}
                        lienholderLogo={
                          lienholders.find(
                            (lh: Lienholder) => lh.display_name === tempInfoData.lienholder,
                          )?.logo_url
                        }
                        otherLienholder={tempInfoData.other_lienholder}
                      />
                    )}
                </SimpleGrid>
                {errors.lienholder && (
                  <Text color="red" fontSize="14px" mt={5}>
                    {errors.lienholder}
                  </Text>
                )}
                {showUnsupportedSellError && (
                  <Text color="leaseEndRed" fontWeight="bold" fontSize="14px" mt={5}>
                    {UNSUPPORTED_LIENHOLDER_SELL_MSG}{' '}
                    <Button variant="link" isLoading={isSubmitting} onClick={submitForm}>
                      Buy it out instead
                    </Button>
                  </Text>
                )}
                <Button
                  variant="link"
                  onClick={() => {
                    rudderanalytics.track(RudderEvent.OtherLienholderModalOpen);
                    setIsModalOpen(true);
                  }}
                  mt="20px"
                >
                  See more options
                </Button>
                <PrimaryButton
                  w="fit-content"
                  type="submit"
                  loading={isSubmitting}
                  disabled={infoLoading}
                  mt="30px"
                />
              </Flex>
              <LienholderModal
                licensePlateState={tempInfoData?.license_plate_state}
                isOpen={isModalOpen}
                closeModal={() => setIsModalOpen(false)}
                lienholders={lienholders}
                handleSubmit={handleSubmit}
              />
              <ChangedLienholderModal
                isOpen={isDifferentLienholderModalOpen}
                closeModal={onDifferentLienholderModalClose}
                handleSubmit={handleSubmit}
              />
            </Form>
          );
        }}
      </Formik>
    </VStack>
  );
};

export default WhoIsYourLease;
